// @ts-nocheck
import { ColumnDef } from '@tanstack/react-table';
import { Crew } from '@/types';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  ArrowUpDown,
  Edit,
  MoreHorizontal,
  TicketIcon,
  Users,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { useDialogManager } from '@/hooks/useDialogManager';
import { cn } from '@/lib/utils';
import { TABLE_STYLES } from '@/lib/tableStyles';
import { SortableHeader } from '@/components/shared/SortableHeader';
import { CREW_STATUS_CONFIG, CrewStatusBadge } from '@/helpers/enumStylers';

// ─── Members dialog ───────────────────────────────────────────────────────────

const CrewMembersDialog = ({ crew }: { crew: Crew }) => {
  const [open, setOpen] = useState(false);
  const count = crew.members?.length ?? 0;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          type="button"
          disabled={count === 0}
          onClick={(e) => e.stopPropagation()}
          className={TABLE_STYLES.pill(count > 0)}
        >
          <Users className="h-3 w-3" />
          {count}
          <span className="text-[10px] opacity-60">members</span>
        </button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-sm rounded-xl p-0 overflow-hidden">
        <DialogHeader className="px-5 py-4 border-b border-border bg-muted/30">
          <DialogTitle className="text-sm font-semibold">
            {crew.name}
          </DialogTitle>
          <p className="text-xs text-muted-foreground mt-0.5">
            {count} member{count !== 1 ? 's' : ''}
          </p>
        </DialogHeader>
        <ScrollArea className="max-h-64">
          <ul className="py-2">
            {crew.members?.map((m) => (
              <li
                key={m.id}
                className="flex items-center gap-3 px-5 py-2.5 hover:bg-muted/40 transition-colors"
              >
                <div className="w-7 h-7 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0">
                  <span className="text-[10px] font-bold text-primary">
                    {m.name?.charAt(0).toUpperCase()}
                  </span>
                </div>
                <span className="text-sm text-foreground">{m.name}</span>
              </li>
            ))}
          </ul>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

// ─── Columns ──────────────────────────────────────────────────────────────────

export const useCrewTableColumns = () => {
  const { openDialog } = useDialogManager();

  return useMemo<ColumnDef<Crew>[]>(
    () => [
      {
        accessorKey: 'name',
        header: ({ column }) => <SortableHeader column={column} title="Crew" />,
        cell: ({ row }) => (
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0">
              <span className="text-xs font-bold text-primary">
                {row.original.name?.charAt(0).toUpperCase()}
              </span>
            </div>
            <div>
              <p className="font-semibold text-sm text-foreground leading-tight">
                {row.original.name}
              </p>
              {row.original.description && (
                <p className="text-xs text-muted-foreground truncate max-w-[200px]">
                  {row.original.description}
                </p>
              )}
            </div>
          </div>
        ),
      },
      {
        accessorKey: 'operationalStatus',
        filterFn: 'equals',
        header: ({ column }) => (
          <SortableHeader column={column} title="Status" />
        ),
        cell: ({ row }) => (
          <CrewStatusBadge status={row.original.operationalStatus} />
        ),
      },
      {
        accessorKey: 'members',
        header: () => <span className={TABLE_STYLES.headerCell}>Members</span>,
        cell: ({ row }) => <CrewMembersDialog crew={row.original} />,
        size: 140,
      },
      {
        id: 'tickets',
        header: () => <span className={TABLE_STYLES.headerCell}>Tickets</span>,
        cell: ({ row }) => {
          const count = row.original.tickets?.length ?? 0;
          return (
            <Link
              to={`/admin/ticket-map?crewId=${row.original.id}`}
              onClick={(e) => e.stopPropagation()}
              className={TABLE_STYLES.pill(count > 0)}
            >
              <TicketIcon className="h-3 w-3" />
              {count}
              <span className="text-[10px] opacity-60">tickets</span>
            </Link>
          );
        },
        size: 140,
      },
      {
        id: 'actions',
        size: 52,
        cell: ({ row }) => (
          <div className="flex justify-end">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-muted-foreground hover:text-foreground"
                  onClick={(e) => e.stopPropagation()}
                >
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-40">
                <DropdownMenuItem
                  onClick={() => openDialog('edit-crew', row.original.id)}
                  className="gap-2 text-sm"
                >
                  <Edit className="h-3.5 w-3.5" />
                  Edit Crew
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ),
      },
    ],
    [openDialog],
  );
};
